import { useCallback, useEffect, useMemo } from 'react'

import { F0Box, F0Text } from '@factorialco/f0-react'
import {
  DynamicFormFieldFragment,
  EmployeeReferenceContractDocument,
} from 'generated/resources/graphql'
import { camelCase, get } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { DynamicField } from 'modules/dynamic_forms/components/DynamicField'
import { useDynamicFormFieldConfigs } from 'modules/dynamic_forms/hooks/useDynamicFormFieldConfigs'
import { useDynamicFormState } from 'modules/dynamic_forms/hooks/useDynamicFormState'
import { CapabilityFilterRule, DynamicFormValues } from 'modules/dynamic_forms/types'

import {
  StepActionContentComponent,
  parseConfigurationJson,
} from 'modules/inbox_tasks/tasks/employeeLifecycleStepAction/helpers'

import { registerDataInputSubmitHandler } from './submitRegistry'
import { DataInputFieldValue, useDataInputUpdate } from './useDataInputUpdate'
import WeekdaysSelector from './WeekdaysSelector'

const DEFAULT_FORM_NAME = 'smart_onboarding'
const CONTRACT_FIELD_PREFIX = 'contracts_'

// Custom-field slugs (40-char hex UUIDs) aren't routed through the standard
// UpdateEmployee / FixContractsContractVersion mutations we use here — hide
// them so authors can't accidentally configure one into a data_input task.
const isCustomFieldSlug = (fieldName: string): boolean => /^[0-9a-f]{40}$/.test(fieldName)

const isContractField = (fieldName: string): boolean => fieldName.startsWith(CONTRACT_FIELD_PREFIX)

// camelCase of the schema field name with the leading "contracts_" prefix
// stripped. e.g. "contracts_working_week_days" → "workingWeekDays". The
// stripped form is also the variable name on FixContractsContractVersion.
const toContractMutationKey = (fieldName: string): string =>
  camelCase(fieldName.slice(CONTRACT_FIELD_PREFIX.length))

// Some data sources (notably `Countries`) emit allowedValue keys with a
// different case than how the value is stored on the employee row (e.g.
// allowedValue `key: "US"` vs stored `nationality: "us"`). Match the stored
// value to the canonical allowedValue key case-insensitively so the Select
// can locate and display the option. Returns the original value if no match
// (so non-enum fields and unknown values fall through unchanged).
const normalizeAllowedValueCase = (
  value: string,
  allowedValues: DynamicFormFieldFragment['allowedValues']
): string => {
  if (!allowedValues || allowedValues.length === 0) return value
  const lower = value.toLowerCase()
  const match = allowedValues.find((option) => option.key.toLowerCase() === lower)
  return match ? match.key : value
}

type DataInputConfiguration = {
  formName: string
  groupKey: string | null
  fieldNames: string[]
}

const parseDataInputConfiguration = (configurationJson?: string | null): DataInputConfiguration => {
  const config = parseConfigurationJson(configurationJson)

  const formName = typeof config?.form_name === 'string' ? config.form_name : DEFAULT_FORM_NAME
  const groupKey = typeof config?.group_key === 'string' ? config.group_key : null
  const fieldNames = Array.isArray(config?.field_names)
    ? config.field_names.filter((name): name is string => typeof name === 'string')
    : []

  return { formName, groupKey, fieldNames }
}

const DataInputContent: StepActionContentComponent = ({ taskId, affectee, configurationJson }) => {
  const session = useCurrentSession()
  const accessId = session?.access?.id ?? null

  // The form always reads from / writes to the affectee (the new hire),
  // regardless of who the assignee is. Contract fields additionally require
  // assignee != affectee — enforced server-side by the data_input handler.
  const affecteeEmployeeId = useMemo(() => {
    const rawId = affectee?.id
    if (rawId == null) return null
    return String(rawId)
  }, [affectee?.id])

  // The data-input config (form_name / group_key / field_names) lives on the
  // StepAction `configuration`, surfaced as `configurationJson` on the taskable.
  const dataInputConfig = useMemo(
    () => parseDataInputConfiguration(configurationJson),
    [configurationJson]
  )

  // Prefill query: standard employee + reference-contract values. Bypasses
  // the data-dispatcher schema-mapper read path (whose bulk-edit gate would
  // filter the affectee out for non-admin self-service actors). See the PR
  // description's "Known limitation" section for the deferred mapper fix.
  const { data: prefillData, loading: prefillLoading } = useQuery(
    EmployeeReferenceContractDocument,
    {
      variables: { employeeIds: affectee?.id != null ? [affectee.id] : [] },
      skip: affectee?.id == null,
    }
  )

  const employeeNode = prefillData?.employees?.employeesConnection?.nodes?.[0] ?? undefined
  const referenceContract = employeeNode?.referenceContract ?? undefined
  const contractVersionId = referenceContract?.id ?? null

  const countries = useMemo<string[] | undefined>(() => {
    const country = employeeNode?.legalEntity?.country
    if (!country) return undefined
    return [country.toLowerCase()]
  }, [employeeNode])

  const rules = useMemo<CapabilityFilterRule[]>(() => {
    const list: CapabilityFilterRule[] = [
      { key: 'form', values: [dataInputConfig.formName], operation: 'include' },
    ]
    if (dataInputConfig.groupKey) {
      list.push({ key: 'group', values: [dataInputConfig.groupKey], operation: 'include' })
    }
    return list
  }, [dataInputConfig.formName, dataInputConfig.groupKey])

  // Schema metadata only (labels, fieldType, allowedValues). The schema-config
  // path is not edit-filtered, so it works for every actor regardless of
  // bulk-edit permission.
  const { fields, loading: schemaLoading } = useDynamicFormFieldConfigs({
    capability: 'dynamic_forms',
    countries,
    rules,
    skip: affecteeEmployeeId === null,
  })

  const configuredFieldNames = useMemo(
    () => new Set(dataInputConfig.fieldNames),
    [dataInputConfig.fieldNames]
  )

  const visibleFields = useMemo(() => {
    const standardFields = fields.filter(
      (field: DynamicFormFieldFragment) => !isCustomFieldSlug(field.fieldName)
    )
    if (configuredFieldNames.size === 0) {
      return standardFields
    }
    return standardFields.filter((field) => configuredFieldNames.has(field.fieldName))
  }, [fields, configuredFieldNames])

  // Build initialValues from the prefill query. Form keys are camelCase of the
  // schema field name; values come from the employee node for personal-info
  // fields and from referenceContract for "contracts_*" fields.
  const initialValues = useMemo<DynamicFormValues>(() => {
    if (!employeeNode) return {}
    const next: DynamicFormValues = {}
    for (const field of visibleFields) {
      const formKey = camelCase(field.fieldName)
      const source: unknown = isContractField(field.fieldName)
        ? get(referenceContract, toContractMutationKey(field.fieldName))
        : get(employeeNode, formKey)
      if (source === null || source === undefined) continue
      if (typeof source === 'boolean') {
        next[formKey] = source
        continue
      }
      next[formKey] = normalizeAllowedValueCase(String(source), field.allowedValues)
    }
    return next
  }, [employeeNode, referenceContract, visibleFields])

  const { values, errors, handleChange, handleToggle, setFieldError, validate } =
    useDynamicFormState({ initialValues })

  const { submit } = useDataInputUpdate()

  const submitForCompletion = useCallback(async (): Promise<boolean> => {
    if (affecteeEmployeeId === null || accessId === null || visibleFields.length === 0) {
      return false
    }

    if (!validate(visibleFields)) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('employee_lifecycle.workflows.data_input.validation_error'),
      })
      return false
    }

    // Convert form values to mutation-variable keys: personal-info form keys
    // map 1:1 to UpdateEmployee variables; contract form keys drop the
    // "contracts" prefix to match FixContractsContractVersion variables.
    const submitValues: Record<string, DataInputFieldValue> = {}
    for (const field of visibleFields) {
      const formKey = camelCase(field.fieldName)
      if (!Object.prototype.hasOwnProperty.call(values, formKey)) continue
      const rawValue = values[formKey]
      if (rawValue === undefined) continue
      const mutationKey = isContractField(field.fieldName)
        ? toContractMutationKey(field.fieldName)
        : formKey
      if (typeof rawValue === 'string' || typeof rawValue === 'boolean') {
        submitValues[mutationKey] = rawValue
      }
    }

    const result = await submit({
      employeeId: affecteeEmployeeId,
      accessId: String(accessId),
      contractVersionId,
      values: submitValues,
    })

    if (!result.success) {
      // Map mutation field errors back to the form by matching mutation
      // variable names to schema field camelCase form keys.
      for (const fieldError of result.fieldErrors) {
        if (!fieldError.field) continue
        const firstMessage = fieldError.messages[0]
        if (!firstMessage) continue
        const formKey = guessFormKeyForMutationField(fieldError.field, visibleFields)
        if (formKey) {
          setFieldError(formKey, firstMessage)
        }
      }
      app.setFlash({
        type: 'wrong',
        message:
          result.message ??
          result.fieldErrors[0]?.messages?.[0] ??
          i18n.t('employee_lifecycle.workflows.data_input.save_error'),
      })
      return false
    }

    return true
  }, [
    accessId,
    affecteeEmployeeId,
    contractVersionId,
    setFieldError,
    submit,
    validate,
    values,
    visibleFields,
  ])

  // Register the real submit handler when there are fields to write back.
  // For the empty branches (no affectee / no eligible fields) register a
  // no-op pass-through instead — the task is misconfigured, but "Complete"
  // must still advance the workflow rather than permanently stall it. The
  // hard-block-on-no-handler path in `employeeLifecycleStepAction/index.tsx`
  // (which catches "panel was never mounted") would otherwise fire here too
  // and trap the user on the no-fields message.
  //
  // Critically: do NOT register the no-op while the schema/prefill is still
  // loading — during that window `visibleFields.length` is transiently 0
  // even for well-configured tasks, and a "mark-as-done" dropdown click in
  // that race would otherwise advance the workflow with no data persisted.
  // We deliberately leave the registration off while loading so the
  // hard-block-on-no-handler flash ("Open the task to fill in the form")
  // fires instead.
  useEffect(() => {
    if (schemaLoading || prefillLoading) return
    if (affecteeEmployeeId === null || visibleFields.length === 0) {
      return registerDataInputSubmitHandler(taskId, async () => true)
    }
    return registerDataInputSubmitHandler(taskId, submitForCompletion)
  }, [
    affecteeEmployeeId,
    prefillLoading,
    schemaLoading,
    submitForCompletion,
    taskId,
    visibleFields.length,
  ])

  if (affecteeEmployeeId === null) {
    return (
      <F0Box display="flex" flexDirection="column" gap="md">
        <F0Text
          variant="body"
          content={i18n.t('employee_lifecycle.workflows.data_input.no_employee')}
        />
      </F0Box>
    )
  }

  if (schemaLoading || prefillLoading) {
    return null
  }

  if (visibleFields.length === 0) {
    return (
      <F0Box display="flex" flexDirection="column" gap="md">
        <F0Text
          variant="body"
          content={i18n.t('employee_lifecycle.workflows.data_input.no_fields')}
        />
      </F0Box>
    )
  }

  return (
    <F0Box display="flex" flexDirection="column" gap="md">
      {visibleFields.map((field) => {
        const formKey = camelCase(field.fieldName)
        // Intercept `contracts_working_week_days` with a controlled F0-native
        // weekday selector — `DynamicField` has no `comma_separated` multi-
        // value branch yet (foundations-shared change deferred to follow-up).
        if (field.fieldName === 'contracts_working_week_days') {
          const raw = values[formKey]
          return (
            <WeekdaysSelector
              key={field.fieldName}
              label={field.label}
              required={field.required}
              error={errors[formKey]}
              value={typeof raw === 'string' ? raw : ''}
              onChange={(next) => handleChange(formKey, next)}
            />
          )
        }
        return (
          <DynamicField
            key={field.fieldName}
            field={field}
            values={values}
            onChange={handleChange}
            onToggle={handleToggle}
            errors={errors}
          />
        )
      })}
    </F0Box>
  )
}

// `SetBankingInformation` reports structured errors using the mutation's
// variable names (`accountNumber` / `format` / `complementaryData`), which
// don't match the dynamic_forms schema field names (`bank_number` /
// `bank_number_format` / `swift_bic` → form keys `bankNumber` /
// `bankNumberFormat` / `swiftBic`). Map mutation-variable → form-key so
// banking backend errors land on the right input inline instead of only in
// the flash. The other two mutations share their argument names with the
// form keys (UpdateEmployee) or with the stripped contract field name
// (FixContractsContractVersion) so they don't need explicit aliases.
const BANKING_FIELD_ALIASES: Readonly<Record<string, string>> = {
  accountNumber: 'bankNumber',
  format: 'bankNumberFormat',
  complementaryData: 'swiftBic',
}

// Map a mutation field name back to its form key:
//   - banking: mutation `accountNumber` ↔ form `bankNumber` (aliased)
//   - personal-info: mutation `firstName` ↔ form `firstName`
//   - contract: mutation `workingWeekDays` ↔ form `contractsWorkingWeekDays`
//
// Backend resolvers vary in casing for `StructuredError.field` — some return
// camelCase (`firstName`), others snake_case (`identifier_type`). Normalize
// the incoming name to camelCase before comparing so both shapes match.
//
// Exported for unit testing — not used outside this file in production.
// Accepts the narrowest field shape the matcher actually reads, so unit
// tests don't have to construct a full DynamicFormFieldFragment.
export function guessFormKeyForMutationField(
  mutationField: string,
  fields: ReadonlyArray<Pick<DynamicFormFieldFragment, 'fieldName'>>
): string | null {
  const targetCamel = camelCase(mutationField)
  const aliased = BANKING_FIELD_ALIASES[targetCamel]
  if (aliased) return aliased
  for (const field of fields) {
    const formKey = camelCase(field.fieldName)
    const candidate = isContractField(field.fieldName)
      ? toContractMutationKey(field.fieldName)
      : formKey
    if (candidate === targetCamel) return formKey
  }
  return null
}

export default DataInputContent
